import { useTranslation } from "@shared/utils/internationalization";
import { useSeatingState } from "@shared/modules/seating/contexts/useSeatingState";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Button from "@ui-kit/form/Button/Button.web";
import ScrollContainer from "@ui-kit/layout/ScrollContainer/ScrollContainer.web";
import Text from "@ui-kit/typography/Text/Text.web";
import Tooltip from "@ui-kit/overlays/Tooltip/Tooltip.web";
import StaticAlert from "@ui-kit/feedback/alerts/StaticAlert/StaticAlert.web";
import CartItem from "@shared/modules/seating/components/SeatingCartItem/CartItem/CartItem.web";
import CartPendingItem from "@shared/modules/seating/components/SeatingCartItem/CartPendingItem/CartPendingItem.web";
import CartTimer from "@shared/components/application/data-display/CartTimer/CartTimer.web";
import CheckoutLabel from "@ui-application/data-display/CheckoutLabel/CheckoutLabel.web";
import { useBasketService } from "@shared/modules/basket/context/BasketServiceContext";
import { IBasket } from "@shared/modules/basket/interfaces/IBasket";
import { SeatingCartProps } from "./SeatingCart.props";
import { useSeatingCart } from "./useSeatingCart";
import SeatingCartTotals from "../SeatingCartTotals/SeatingCartTotals.web";

const SeatingCart = ({
  venueData,
  overridePurchaseLimits = undefined,
  hideCheckoutButton = false,
  onCheckoutCb,
}: SeatingCartProps) => {
  const { t } = useTranslation("seating");
  const { countOfSelectedItems, isLoadingMap } = useSeatingState();

  const { isBasketUpdating, isWaitlist, totalItemQuantity } =
    useBasketService<IBasket>();

  const {
    isEmptyCart,
    pendingMinPurchaseSeats,
    currentMapItemsInCart,
    minPurchaseSeatArray,
    minPurchaseLimit,
    minPurchaseInProgress,
    checkoutTooltipText,
    onCheckout,
  } = useSeatingCart({ venueData, overridePurchaseLimits, onCheckoutCb });

  return (
    <>
      {/* Header With Timer */}
      <Flex
        justifyContent="space-between"
        alignContent="center"
        padding="20px 20px 10px 20px"
        height="fit-content"
        fontSize="12px !important"
      >
        <Text hidden={isEmptyCart} fontWeight="bold">
          {t("{{count}} items selected", {
            count: countOfSelectedItems,
          })}
        </Text>
        <Flex>{!isEmptyCart && !isLoadingMap && <CartTimer.Seating />}</Flex>
      </Flex>
      {/* Scroll container with seating items in the basket */}
      <ScrollContainer hidden={isEmptyCart} showFade>
        {/* Displays minimum purchase seats that are pending selection */}
        {pendingMinPurchaseSeats.map((pendingSeat) => (
          <CartPendingItem
            key={pendingSeat.$data.id}
            seatItem={pendingSeat}
            isMultiItemLimit={overridePurchaseLimits?.multiItemsAllowed}
          />
        ))}
        {/* Displays ONLY the cart items that are from the current seating map page */}
        {currentMapItemsInCart.map((cartItem) => (
          <CartItem
            key={cartItem.seat ? cartItem.seat.id : cartItem.itemGroup?.id}
            formattedData={cartItem}
            venueData={venueData}
            disableItemDeletion={
              minPurchaseSeatArray.length > 0 ||
              isBasketUpdating ||
              isLoadingMap
            }
          />
        ))}
      </ScrollContainer>
      {/* Totals container with fee breakdown */}
      <Flex direction="column">
        {isWaitlist && !isEmptyCart && (
          <StaticAlert
            status="info"
            title={t("Items will not be issued when joining the waitlist")}
            size="sm"
          />
        )}
        <Flex
          p="5px 20px 20px 20px"
          direction="column"
          borderTop="1px solid"
          borderColor="gray.400"
        >
          <SeatingCartTotals
            venueData={venueData}
            overridePurchaseLimits={overridePurchaseLimits}
            minPurchaseLimit={minPurchaseLimit}
            minPurchaseInProgress={minPurchaseInProgress}
            minPurchaseSeatArray={minPurchaseSeatArray}
            isEmptyCart={isEmptyCart}
          />
          {!hideCheckoutButton && (
            <Tooltip
              p="sm"
              backgroundColor="gray.950"
              label={checkoutTooltipText}
              hasArrow
              placement="bottom"
            >
              <Flex mt="lg">
                <Button
                  data-testid="seating-cart-checkout"
                  variant="solid"
                  colorPalette="primary"
                  width="100%"
                  minHeight="element.3xl"
                  disabled={
                    isEmptyCart || minPurchaseInProgress || isBasketUpdating
                  }
                  onClick={onCheckout}
                >
                  <CheckoutLabel
                    totalItemQuantity={totalItemQuantity}
                    isWaitlist={isWaitlist}
                  />
                </Button>
              </Flex>
            </Tooltip>
          )}
        </Flex>
      </Flex>
    </>
  );
};

export default SeatingCart;
